<script lang="ts">
import {
  computed,
  defineComponent,
  inject,
  nextTick,
  onMounted,
  ref,
  toRef,
  watch,
} from 'vue'
import { useField } from 'vee-validate'

let id = 0

export default defineComponent({
  name: 'base-input',
  inheritAttrs: false,
  props: {
    modelValue: {
      type: [String, Number],
      default: '',
    },
    raw: {
      type: [String, Number],
      default: '',
    },
    label: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    name: {
      type: String,
      default: '',
    },
    type: {
      type: String,
      default: '',
    },
    rules: {
      type: [String, Object],
      default: '',
    },
    error: {
      type: String,
      default: '',
    },
    maxlength: {
      type: Number,
      default: undefined,
    },
    required: {
      type: Boolean,
      default: false,
    },
    classes: {
      type: Object,
      default: () => ({}),
    },
    currency: {
      type: [Object, Boolean],
      default: false,
    },
    useRaw: {
      type: Boolean,
      default: true,
    },
    placeholder: {
      type: String,
      default: '',
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    showCounter: {
      type: Boolean,
      default: true,
    },
    autocomplete: {
      type: String,
      default: 'off',
    },
    staticErrorSlot: {
      type: Boolean,
      default: false,
    },
    isFloatLabel: {
      type: Boolean,
      default: false,
    },
    look: {
      type: String,
      default: '',
      validator: (v: string) => !v || /clear|dense/.test(v),
    },
  },
  emits: [
    'update:modelValue',
    'update:raw',
  ],
  setup(props, ctx) {
    const rawValue = ref<string | number>('')
    const inputValue = ref<string | number>('')
    const element = ref({} as HTMLElement)
    const name = toRef(props, 'name')
    const label = toRef(props, 'label')

    const isPlaceholderVisible = ref(false)

    const inputId = (() => {
      id += 1
      return `input-${id}`
    })()

    const rules = computed(() => {
      if (props.disabled) return ''
      if (props.rules) return props.rules
      return props.required ? 'required' : ''
    })

    const form: any = inject('form', null)

    const vee = props.name && form
      ? useField(name.value, rules.value, {
        validateOnMount: false,
        validateOnValueUpdate: false,
        label: label.value,
      })
      : null

    const isFocused = ref(false)
    const isDisabled = computed(() => form?.disabled || props.disabled)
    const isNumber = computed(() => props.type === 'number')

    const isLookClear = computed(() => {
      return props.look === 'clear'
    })

    const isLabelExpanded = computed(() => {
      return isFocused.value || Boolean(inputValue.value)
    })

    const errorProxy = ref('')

    const errorMessage = computed(() => {
      return isDisabled.value ? '' : (props.error || vee?.errorMessage.value)
    })

    const placeholderProxy = computed(() => {
      if (props.isFloatLabel) {
        if (!isPlaceholderVisible.value || (props.label && !isLabelExpanded.value)) {
          return ''
        }
      }

      return props.placeholder
    })

    const typeProxy = computed(() => {
      return props.currency ? 'text' : props.type
    })

    const isRequired = computed(() => {
      if (isDisabled.value) return false
      if (rules.value) {
        if (typeof rules.value === 'string') {
          return /required/.test(rules.value)
        } else if (typeof rules.value === 'object') {
          return !!rules.value?.required
        }
      }

      return props.required
    })

    function setValue(_value: any) {
      let value

      if (typeof _value === 'number') {
        value = isNaN(_value) ? '' : _value
      } else {
        value = _value ?? ''
      }

      inputValue.value = value
      rawValue.value = value

      if (props.currency) {
        vee?.handleChange(inputValue.value, false)
      } else {
        vee?.handleChange(props.useRaw ? rawValue.value : inputValue.value, false)
      }

      if (errorMessage.value) {
        vee?.validate()
      }

      ctx.emit('update:modelValue', inputValue.value)
      ctx.emit('update:raw', rawValue.value)
    }

    function setInitialValue() {
      nextTick(() => {
        const value = props.raw || props.modelValue || vee?.value.value

        if (value) {
          setValue(value)
        }
      })
    }

    onMounted(async () => {
      setInitialValue()
    })

    watch(() => props.modelValue, modelPropValue => {
      if (modelPropValue !== inputValue.value) {
        setValue(modelPropValue)
      }
    })

    {
      let timer: any = 0

      watch(isFocused, (v) => {
        if (timer) clearTimeout(timer)

        if (v) {
          timer = setTimeout(() => {
            isPlaceholderVisible.value = true
          }, 150)
        } else {
          isPlaceholderVisible.value = false
        }
      })
    }

    if (form && vee) {
      watch(() => vee.value.value, newValue => {
        if (props.useRaw && newValue !== rawValue.value) {
          return setValue(newValue)
        }

        if (!props.useRaw && newValue !== inputValue.value) {
          setValue(newValue)
        }
      })
    }

    watch(() => props.raw, rawPropValue => {
      if (rawPropValue !== rawValue.value) {
        setValue(rawPropValue)
      }
    })

    return {
      inputId,
      inputValue,
      element,
      errorMessage,
      errorProxy,
      placeholderProxy,
      isDisabled,
      isNumber,
      isRequired,
      typeProxy,
      isLookClear,
      isLabelExpanded,
      handlers: {
        input: (e: any) => {
          const value = e.target?.value

          e.stopPropagation()
          e.stopImmediatePropagation()
          e.preventDefault()

          setValue(isNumber.value ? parseFloat(value) : value)
        },
        change: () => {
          vee?.handleChange(rawValue.value)
        },
        focus: () => {
          isFocused.value = true
        },
        blur: (e: any) => {
          isFocused.value = false

          if (!isNumber.value) {
            setValue(String(e.target.value ?? '').trim())
          }

          if (name.value && props.rules && inputValue.value) {
            vee?.handleBlur()
            vee?.validate()
          }
        },
      },
    }
  },
})
</script>

<template>
  <div
    ref="root"
    :data-input-name="name"
    class="relative"
  >
    <label
      v-if="label"
      :for="inputId"
      class="block max-w-full truncate border-0 text-label mb-label"
    >
      {{ label }} {{  required ? '*' : '' }}
    </label>
    <div
      :class="[
        isDisabled ? '' : '',
        !readonly && 'focus-within:ring-1 focus-within:ring-input-focused focus-within:border-input-focused',
        isLookClear && 'border-0',
      ]"
      class="flex z-10 items-center text-lg rounded border h-input bg-input border-input"
    >
      <div
        v-if="$slots.left"
        class="flex shrink-0 items-center h-full"
      >
        <slot name="left" />
      </div>
      <div class="relative flex-1 shrink-0 h-full">
        <input
          :id="inputId"
          ref="element"
          :autocomplete="autocomplete"
          :class="[
            !$slots.left && 'rounded-l',
            !$slots.right && 'rounded-r',
          ]"
          :disabled="isDisabled"
          :maxlength="maxlength"
          :name="name"
          :placeholder="placeholderProxy"
          :readonly="readonly"
          :type="typeProxy"
          :value="inputValue"
          class="px-3 w-full h-full font-sans truncate bg-transparent disabled:bg-transparent focus:outline-none disabled:opacity-50 appearance-none disabled:cursor-not-allowed text-input text-default interactive placeholder:text-placeholder focus:border-input-focused"
          v-bind="$attrs"
          v-on="handlers"
        >
      </div>

      <div
        v-if="$slots.right"
        class="flex shrink-0 items-center pl-4 h-full"
      >
        <slot name="right" />
      </div>
    </div>
    <div
      v-if="staticErrorSlot || (showCounter && maxlength) || errorProxy || errorMessage || $slots.default"
      class="flex pt-px text-sm leading-5"
    >
      <div class="mt-1 w-full leading-tight">
        <slot
          :error="errorMessage"
          name="error"
        >
          <div
            :class="[
              staticErrorSlot && 'min-h-5'
            ]"
            class="text-danger"
            data-error
          >
            {{ errorMessage || errorProxy }}
          </div>
        </slot>
      </div>

      <div
        v-if="maxlength && showCounter"
        class="shrink-0 ml-6 leading-5 text-right min-w-24 text-light"
      >
        <span>{{ String(inputValue).length }}</span>
        <span class="mx-px">
          /
        </span>
        <span>{{ maxlength }}</span>
      </div>
    </div>
  </div>
</template>
